import { type RefObject, useCallback, useLayoutEffect, useRef } from 'react';

const getScrollParent = (element: HTMLElement) => {
  for (let parent = element.parentElement; parent; parent = parent.parentElement) {
    const { overflowY } = window.getComputedStyle(parent);
    if (overflowY === 'auto' || overflowY === 'scroll') return parent;
  }

  return document.scrollingElement;
};

// Keeps `elementRef` at the same spot on screen across a content change (e.g. switching table pages),
// so the content above it can grow or shrink without the element jumping away from the pointer.
export const useKeepInViewport = (elementRef: RefObject<HTMLElement | null>, changeKey: unknown) => {
  const topBeforeChange = useRef<number | null>(null);

  const rememberPosition = useCallback(() => {
    topBeforeChange.current = elementRef.current?.getBoundingClientRect().top ?? null;
  }, [elementRef]);

  // biome-ignore lint/correctness/useExhaustiveDependencies: re-anchor only after the content identified by `changeKey` changed
  useLayoutEffect(() => {
    const element = elementRef.current;
    const previousTop = topBeforeChange.current;
    topBeforeChange.current = null;

    if (!element || previousTop === null) return;

    const scrollParent = getScrollParent(element);
    if (scrollParent) scrollParent.scrollTop += element.getBoundingClientRect().top - previousTop;
  }, [changeKey]);

  return rememberPosition;
};
